import { Fragment } from "react";
import { formatEUR } from "@/lib/i18n";
import { useLang } from "@/context/LanguageContext";
import Sparkline from "@/components/wizard/Sparkline";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "@/components/ui/table";

const deltaStyle = (delta) => {
  if (delta === null || delta === undefined) return "text-slate-400";
  if (delta > 0) return "text-green-700";
  if (delta < 0) return "text-red-700";
  return "text-slate-500";
};

export default function BalanceSheetTable({ title, rows, lang, accent = "blue" }) {
  const { t } = useLang();
  const filtered = (rows || []).filter((r) =>
    r.items?.length ||
    r.total !== 0 ||
    (r.prior_total || 0) !== 0 ||
    (r.prior_years || []).some((py) => py.total !== 0)
  );
  const macroColor = accent === "slate" ? "text-slate-700" : "text-blue-700";

  // Multi-year mode: category has prior_years array (2+ prior years)
  const multiYear = filtered.some((r) => Array.isArray(r.prior_years) && r.prior_years.length > 0);
  const priorYearsMeta = multiYear
    ? (filtered.find((r) => r.prior_years?.length)?.prior_years || [])
    : [];

  const hasPrior = !multiYear && filtered.some((r) => r.prior_total !== undefined);

  // Dual-column CE (Provvisorio + Proiezione) — annualized interim statement
  const dualCE = filtered.some((r) => r.total_raw !== undefined && r.total_raw !== null);

  const currentTotal = filtered.reduce((acc, r) => acc + (r.total || 0), 0);
  const currentRawTotal = dualCE ? filtered.reduce((acc, r) => acc + (r.total_raw || 0), 0) : 0;
  const priorTotal = filtered.reduce((acc, r) => acc + (r.prior_total || 0), 0);
  const priorYearsTotals = priorYearsMeta.map((py, idx) =>
    filtered.reduce((acc, r) => acc + ((r.prior_years?.[idx]?.total) || 0), 0)
  );

  return (
    <section className="rounded-xl border border-slate-200 bg-white" data-testid={`balance-sheet-${accent}`}>
      <header className="border-b border-slate-100 px-5 py-4">
        <h2 className={`font-heading text-base font-semibold tracking-tight ${macroColor}`}>{title}</h2>
      </header>
      <div className="overflow-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16 text-xs">Cod.</TableHead>
              <TableHead className="text-xs">{lang === "it" ? "Voce" : "Item"}</TableHead>
              {dualCE ? (
                <>
                  <TableHead className="w-32 text-right text-xs" title={t("prov_hint")}>{t("prov_column")}</TableHead>
                  <TableHead className="w-36 bg-amber-50/50 text-right text-xs font-semibold text-amber-900" title={t("proj_hint")}>
                    {t("proj_column")}
                  </TableHead>
                </>
              ) : (
                <TableHead className="w-36 text-right text-xs">
                  {multiYear || hasPrior ? t("current_year_column") : (lang === "it" ? "Importo" : "Amount")}
                </TableHead>
              )}
              {multiYear && priorYearsMeta.map((py, idx) => (
                <TableHead key={idx} className="w-32 text-right font-mono text-xs">{py.label}</TableHead>
              ))}
              {multiYear && !dualCE && (
                <TableHead className="w-28 text-center text-xs">{t("sparkline_col")}</TableHead>
              )}
              {hasPrior && !dualCE && (
                <>
                  <TableHead className="w-32 text-right text-xs">{t("prior_year_column")}</TableHead>
                  <TableHead className="w-24 text-right text-xs">{t("delta_pct_column")}</TableHead>
                </>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={3 + (dualCE ? 1 : 0) + (multiYear ? priorYearsMeta.length + (dualCE ? 0 : 1) : 0) + (hasPrior && !dualCE ? 2 : 0)} className="py-6 text-center text-sm text-slate-400">
                  —
                </TableCell>
              </TableRow>
            )}
            {filtered.map((cat) => (
              <Fragment key={cat.code}>
                <TableRow className="bg-slate-50/60">
                  <TableCell className="py-2 font-mono text-xs text-blue-700">{cat.code}</TableCell>
                  <TableCell className="py-2 font-medium text-slate-900">
                    {lang === "it" ? cat.name_it : cat.name_en}
                  </TableCell>
                  {dualCE ? (
                    <>
                      <TableCell className="py-2 text-right font-mono text-sm text-slate-600" data-testid={`prov-${cat.code}`}>
                        {formatEUR(cat.total_raw ?? 0, lang)}
                      </TableCell>
                      <TableCell className="py-2 bg-amber-50/40 text-right font-mono text-sm font-semibold text-amber-900" data-testid={`proj-${cat.code}`}>
                        {formatEUR(cat.total ?? 0, lang)}
                      </TableCell>
                    </>
                  ) : (
                    <TableCell className="py-2 text-right font-mono text-sm font-medium text-slate-900">
                      {formatEUR(cat.total, lang)}
                    </TableCell>
                  )}
                  {multiYear && priorYearsMeta.map((_, idx) => (
                    <TableCell key={idx} className="py-2 text-right font-mono text-sm text-slate-600">
                      {formatEUR(cat.prior_years?.[idx]?.total || 0, lang)}
                    </TableCell>
                  ))}
                  {multiYear && !dualCE && (
                    <TableCell className="py-2 text-center">
                      <div className="flex justify-center">
                        <Sparkline
                          values={[...(cat.prior_years || []).slice().reverse().map((p) => p.total || 0), cat.total || 0]}
                        />
                      </div>
                    </TableCell>
                  )}
                  {hasPrior && !dualCE && (
                    <>
                      <TableCell className="py-2 text-right font-mono text-sm text-slate-600">
                        {formatEUR(cat.prior_total, lang)}
                      </TableCell>
                      <TableCell className={`py-2 text-right font-mono text-xs ${deltaStyle(cat.delta_pct)}`}>
                        {cat.delta_pct === null || cat.delta_pct === undefined
                          ? "—"
                          : `${cat.delta_pct > 0 ? "+" : ""}${cat.delta_pct}%`}
                      </TableCell>
                    </>
                  )}
                </TableRow>
                {/* Show leaf items only in single-year mode (and no dual CE) to keep tables readable */}
                {!hasPrior && !multiYear && !dualCE && cat.items?.slice(0, 6).map((it, i) => (
                  <TableRow key={`${cat.code}-${i}`} className="text-slate-600">
                    <TableCell />
                    <TableCell className="py-1.5 pl-6 text-xs">{it.description}</TableCell>
                    <TableCell className="py-1.5 text-right font-mono text-xs">
                      {formatEUR(it.amount, lang)}
                    </TableCell>
                  </TableRow>
                ))}
                {!hasPrior && !multiYear && !dualCE && cat.items?.length > 6 && (
                  <TableRow>
                    <TableCell />
                    <TableCell className="py-1 pl-6 text-[11px] italic text-slate-400">
                      +{cat.items.length - 6} {lang === "it" ? "altre voci" : "more items"}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                )}
              </Fragment>
            ))}
            <TableRow className="bg-blue-50">
              <TableCell />
              <TableCell className="py-3 font-heading text-sm font-semibold uppercase tracking-wider text-blue-900">
                {lang === "it" ? "Totale" : "Total"}
              </TableCell>
              {dualCE ? (
                <>
                  <TableCell className="py-3 text-right font-mono text-sm font-medium text-blue-800">
                    {formatEUR(currentRawTotal, lang)}
                  </TableCell>
                  <TableCell className="py-3 bg-amber-100 text-right font-mono text-sm font-bold text-amber-900">
                    {formatEUR(currentTotal, lang)}
                  </TableCell>
                </>
              ) : (
                <TableCell className="py-3 text-right font-mono text-sm font-bold text-blue-900">
                  {formatEUR(currentTotal, lang)}
                </TableCell>
              )}
              {multiYear && priorYearsTotals.map((pt, idx) => (
                <TableCell key={idx} className="py-3 text-right font-mono text-sm font-medium text-blue-800">
                  {formatEUR(pt, lang)}
                </TableCell>
              ))}
              {multiYear && !dualCE && (
                <TableCell className="py-3 text-center">
                  <div className="flex justify-center">
                    <Sparkline values={[...priorYearsTotals.slice().reverse(), currentTotal]} />
                  </div>
                </TableCell>
              )}
              {hasPrior && !dualCE && (
                <>
                  <TableCell className="py-3 text-right font-mono text-sm font-medium text-blue-800">
                    {formatEUR(priorTotal, lang)}
                  </TableCell>
                  <TableCell className={`py-3 text-right font-mono text-xs font-medium ${deltaStyle(currentTotal - priorTotal)}`}>
                    {priorTotal ? `${currentTotal > priorTotal ? "+" : ""}${(((currentTotal - priorTotal) / priorTotal) * 100).toFixed(2)}%` : "—"}
                  </TableCell>
                </>
              )}
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </section>
  );
}
